import { ReactNode, useEffect, useRef, useState } from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  TooltipProps,
  ResponsiveContainer,
  CartesianGrid,
  DefaultTooltipContent,
  ReferenceArea
} from "recharts";
import { CenterLinePoint, VftCoefficients } from "../../types";
import {
  EMISSIVITY_REFERENCE,
  enrichCenterLinePoint
} from "../../utils/thermoEmissivity";
import { responsivePlotTier, useElementSize } from "../../hooks/useElementSize";
const DRAW_COLORS = ["#6be7c5", "#ff9f9f", "#8ea6ff", "#f7d774", "#9ed2ff"];
const TEMP_MAIN_SERIES = "temperature_display";
const TEMP_UPPER_SERIES = "temperature_upper";
const TEMP_LOWER_SERIES = "temperature_lower";
const VISCOSITY_SERIES = "viscosity";

type DrawnLineProfile = {
  id: string;
  color?: string;
  points: { y: number; temperature: number }[];
};

type Props = {
  data: CenterLinePoint[];
  title: string;
  loading?: boolean;
  mode: "single" | "multi";
  emptyMessage?: string;
  vftCoeffs?: VftCoefficients | null;
  drawnLines?: DrawnLineProfile[];
  xAxisKey?: "y" | "height_mm";
  xAxisLabel?: string;
  actions?: ReactNode;
};

const formatTooltipNumber = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toFixed(2) : String(value);
  }
  if (typeof value === "string") {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric.toFixed(2) : value;
  }
  return String(value);
};

const formatTooltipName = (name: string | number) => {
  const label = String(name);
  const match = label.match(/ε=([0-9.]+)/);
  if (!match) return label;
  const numeric = Number(match[1]);
  if (!Number.isFinite(numeric)) return label;
  return label.replace(match[1], numeric.toFixed(2));
};

const formatAxisTick = (value: unknown, rounded = false) => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (rounded) return `${Math.round(numeric)}`;
  return `${numeric}`;
};

const formatAxisTickMaxTwoDecimals = (value: unknown) => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(2).replace(/\.?0+$/, "");
};

const formatCompactAxisTick = (value: unknown) => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  if (Math.abs(numeric) >= 100) return `${Math.round(numeric)}`;
  if (Math.abs(numeric) >= 10) return numeric.toFixed(1).replace(/\.0$/, "");
  return formatAxisTickMaxTwoDecimals(numeric);
};

const isPlausibleTemperatureValue = (value: number) =>
  Number.isFinite(value) && value > -100 && value < 2_000;

const isPlausibleViscosityValue = (value: number) =>
  Number.isFinite(value) && value > -10 && value < 30;

const paddedDomain = (values: number[], fallback: [number, number]): [number, number] => {
  const finite = values.filter((value) => Number.isFinite(value));
  if (!finite.length) return fallback;
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  if (min === max) {
    const pad = Math.max(Math.abs(min) * 0.05, 1);
    return [min - pad, max + pad];
  }
  const pad = Math.max((max - min) * 0.08, 1);
  return [min - pad, max + pad];
};

const tooltipWithoutBands = (props: TooltipProps<any, any>, xAxisKey: "y" | "height_mm") => {
  const filtered = props.payload?.filter((item) => item.dataKey !== "temperature_band");
  if (!props.active || !filtered || filtered.length === 0) return null;
  return (
    <DefaultTooltipContent
      {...props}
      payload={filtered}
      formatter={(value, name) => [formatTooltipNumber(value), formatTooltipName(name)]}
      labelFormatter={(label) =>
        xAxisKey === "height_mm"
          ? `${formatAxisTick(label, true)} mm`
          : formatAxisTick(label)
      }
      contentStyle={{
        backgroundColor: "var(--surface-elevated)",
        border: "1px solid var(--surface-elevated-border)",
        borderRadius: 10,
        boxShadow: "var(--modal-shadow)",
        color: "var(--text-primary)"
      }}
      labelStyle={{ color: "var(--text-muted)" }}
    />
  );
};

export default function CenterLineChart({
  data,
  title,
  loading,
  mode,
  emptyMessage = "No data",
  vftCoeffs,
  drawnLines = [],
  xAxisKey = "y",
  xAxisLabel,
  actions
}: Props) {
  const [showBands, setShowBands] = useState(true);
  const [hiddenSeries, setHiddenSeries] = useState<string[]>([]);
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
  const [zoomSelection, setZoomSelection] = useState<{ start: number; end: number } | null>(null);
  const [suppressTooltip, setSuppressTooltip] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const lastTapRef = useRef(0);
  const lastZoomDragAtRef = useRef(0);
  const zoomDragRef = useRef(false);
  const cardSize = useElementSize(cardRef);
  const measuredWidth =
    cardSize.width || (typeof window === "undefined" ? 960 : window.innerWidth);
  const plotTier = responsivePlotTier(measuredWidth);
  const isCompact = plotTier !== "wide";
  const isPhone = plotTier === "phone";
  const chartData =
    data?.map((p) => {
      const enriched =
        Number.isFinite(p.temperature_epsilon_0875) &&
        Number.isFinite(p.temperature_upper) &&
        Number.isFinite(p.temperature_lower)
          ? p
          : enrichCenterLinePoint(p, EMISSIVITY_REFERENCE);
      const tempDisplay = enriched.temperature_epsilon_0875 ?? p.temperature;
      const tempUpper = enriched.temperature_upper ?? tempDisplay;
      const tempLower = enriched.temperature_lower ?? tempDisplay;
      const tempBandLow = Math.min(tempUpper, tempLower);
      const tempBandHigh = Math.max(tempUpper, tempLower);
      return {
        ...enriched,
        temperature_display: tempDisplay,
        temperature_band: [tempBandLow, tempBandHigh]
      };
    }) || [];
  const chartHeight = isPhone ? 340 : isCompact ? 320 : 340;
  const chartMargin = isCompact
    ? { top: 10, right: isPhone ? 4 : 12, bottom: xAxisLabel ? 56 : 38, left: isPhone ? 0 : 8 }
    : { top: 14, right: 82, bottom: xAxisLabel ? 60 : 44, left: 70 };
  const isSeriesHidden = (id: string) => hiddenSeries.includes(id);
  const visibleChartData = zoomDomain
    ? chartData.filter((entry) => {
        const value = Number(entry?.[xAxisKey]);
        return Number.isFinite(value) && value >= zoomDomain[0] && value <= zoomDomain[1];
      })
    : chartData;
  const temperatureDomain = paddedDomain(
    [
      ...visibleChartData
        .flatMap((entry) => [
          isSeriesHidden(TEMP_MAIN_SERIES) ? NaN : Number(entry.temperature_display),
          isSeriesHidden(TEMP_UPPER_SERIES) ? NaN : Number(entry.temperature_upper),
          isSeriesHidden(TEMP_LOWER_SERIES) ? NaN : Number(entry.temperature_lower)
        ])
        .filter(isPlausibleTemperatureValue),
      ...drawnLines.flatMap((line) =>
        isSeriesHidden(`drawn:${line.id}`)
          ? []
          : line.points
              .filter((point) => {
                if (!zoomDomain) return true;
                const xValue = Number(point.y);
                return Number.isFinite(xValue) && xValue >= zoomDomain[0] && xValue <= zoomDomain[1];
              })
              .map((point) => point.temperature)
              .filter(isPlausibleTemperatureValue)
      )
    ],
    [0, 1000]
  );
  const viscosityDomain = paddedDomain(
    isSeriesHidden(VISCOSITY_SERIES)
      ? []
      : visibleChartData.map((entry) => Number(entry.viscosity)).filter(isPlausibleViscosityValue),
    [0, 16]
  );
  const resetZoom = () => {
    zoomDragRef.current = false;
    setZoomDomain(null);
    setZoomSelection(null);
    setSuppressTooltip(false);
  };
  useEffect(() => {
    resetZoom();
  }, [data, xAxisKey]);

  const getActiveX = (event: unknown) => {
    const activeLabel = (event as { activeLabel?: unknown } | null)?.activeLabel;
    const numeric = typeof activeLabel === "number" ? activeLabel : Number(activeLabel);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const getMinimumZoomRange = () => {
    const values = chartData
      .map((entry) => Number(entry?.[xAxisKey]))
      .filter((value) => Number.isFinite(value));
    if (values.length < 2) return 1;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const fullRange = Math.abs(max - min);
    return fullRange > 0 ? fullRange * 0.015 : 1;
  };
  const isMeaningfulZoomDrag = (start: number, end: number) =>
    Math.abs(end - start) >= getMinimumZoomRange();
  const handleZoomStart = (event: unknown) => {
    const activeX = getActiveX(event);
    if (activeX == null) return;
    zoomDragRef.current = false;
    setSuppressTooltip(false);
    setZoomSelection({ start: activeX, end: activeX });
  };
  const handleZoomMove = (event: unknown) => {
    const activeX = getActiveX(event);
    if (activeX == null) return;
    setZoomSelection((current) => {
      if (!current) {
        if (Date.now() - lastZoomDragAtRef.current > 700) setSuppressTooltip(false);
        return current;
      }
      if (isMeaningfulZoomDrag(current.start, activeX)) {
        zoomDragRef.current = true;
        setSuppressTooltip(true);
      }
      return { ...current, end: activeX };
    });
  };
  const handleZoomEnd = (event: unknown) => {
    const activeX = getActiveX(event);
    setZoomSelection((current) => {
      if (!current || activeX == null) return null;
      const start = current.start;
      const end = activeX;
      const min = Math.min(start, end);
      const max = Math.max(start, end);
      if (isMeaningfulZoomDrag(start, end)) {
        setZoomDomain([min, max]);
        lastZoomDragAtRef.current = Date.now();
        setSuppressTooltip(true);
      } else {
        setSuppressTooltip(false);
      }
      zoomDragRef.current = false;
      return null;
    });
  };
  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastZoomDragAtRef.current < 500) {
      lastTapRef.current = 0;
      return;
    }
    if (now - lastTapRef.current < 320) {
      resetZoom();
      lastTapRef.current = 0;
      return;
    }
    lastTapRef.current = now;
  };
  const toggleSeries = (id: string) => {
    setHiddenSeries((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id]
    );
  };
  const legendItems = [
    { id: TEMP_MAIN_SERIES, label: "Temperature ε=0.875", color: "#ffb347", dash: "solid" },
    { id: TEMP_UPPER_SERIES, label: "Temperature ε=0.95", color: "#ffd089", dash: "5 3" },
    { id: TEMP_LOWER_SERIES, label: "Temperature ε=0.8", color: "#ff8a3d", dash: "3 3" },
    ...drawnLines.map((line, idx) => ({
      id: `drawn:${line.id}`,
      label: `Drawn line ${idx + 1}`,
      color: line.color || DRAW_COLORS[idx % DRAW_COLORS.length],
      dash: "2 2"
    })),
    { id: VISCOSITY_SERIES, label: "Viscosity log10(η / dPa·s)", color: "#6cb6ff", dash: "solid" }
  ];
  const axisUnitChips = (
    <div
      style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 6,
        fontSize: 12,
        fontWeight: 700
      }}
    >
      <span style={{ color: "#ffb347" }}>Temperature (°C)</span>
      <span style={{ color: "#6cb6ff" }}>Viscosity log10(η / dPa·s)</span>
    </div>
  );

  return (
    <div ref={cardRef} className="card thermo-centerline-chart-card">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 8,
          flexWrap: "wrap"
        }}
      >
        <h3 className="section-title">{title}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
          {actions}
          <button className="button secondary" onClick={() => setShowBands((v) => !v)}>
            {showBands ? "Hide emissivity bands" : "Show emissivity bands"}
          </button>
          <button className="button secondary" onClick={resetZoom}>
            Reset zoom
          </button>
          <span className="pill">{mode === "single" ? "Single sample" : "Multi-selection"}</span>
        </div>
      </div>
      {loading && <div>Loading center line…</div>}
      {!loading && !data.length && <div style={{ color: "var(--text-faint)" }}>{emptyMessage}</div>}
      {!loading && data.length > 0 && (
        <>
          {isCompact ? axisUnitChips : null}
          <div
            className="centerline-chart-zoom-region"
            onTouchEnd={handleDoubleTap}
            onDoubleClick={resetZoom}
          >
          <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart
            data={chartData}
            margin={chartMargin}
            onMouseDown={handleZoomStart}
            onMouseMove={handleZoomMove}
            onMouseUp={handleZoomEnd}
            onMouseLeave={() => {
              if (!zoomDragRef.current && Date.now() - lastZoomDragAtRef.current > 700) {
                setSuppressTooltip(false);
              }
              setZoomSelection(null);
              zoomDragRef.current = false;
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#1e2b3d" />
            <XAxis
              dataKey={xAxisKey}
              type="number"
              domain={zoomDomain ?? ["dataMin", "dataMax"]}
              allowDataOverflow
              stroke="#7e8ca3"
              tickFormatter={(value) =>
                xAxisKey === "height_mm"
                  ? formatAxisTick(value, true)
                  : formatAxisTickMaxTwoDecimals(value)
              }
              label={
                xAxisLabel
                  ? {
                      value: xAxisLabel,
                      position: "bottom",
                      offset: 26,
                      fill: "#7e8ca3"
                    }
                  : undefined
              }
            />
            <YAxis
              yAxisId="left"
              stroke="#ffb347"
              width={isPhone ? 58 : 58}
              domain={temperatureDomain}
              allowDataOverflow
              tickFormatter={isCompact ? formatCompactAxisTick : formatAxisTickMaxTwoDecimals}
              label={
                isCompact
                  ? undefined
                  : {
                      value: "Temperature (°C)",
                      angle: -90,
                      position: "left",
                      fill: "#ffb347"
                    }
              }
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#6cb6ff"
              width={isPhone ? 54 : 76}
              domain={viscosityDomain}
              allowDataOverflow
              tickFormatter={isCompact ? formatCompactAxisTick : formatAxisTickMaxTwoDecimals}
              label={
                isCompact
                  ? undefined
                  : {
                      value: "Viscosity log10(η / dPa·s)",
                      angle: 90,
                      position: "right",
                      fill: "#6cb6ff"
                    }
              }
            />
            <Tooltip
              active={suppressTooltip ? false : undefined}
              content={(props) =>
                suppressTooltip ? null : tooltipWithoutBands(props, xAxisKey)
              }
            />
            {showBands && !isSeriesHidden(TEMP_MAIN_SERIES) && (
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="temperature_band"
                fill="#ffb347"
                stroke="none"
                fillOpacity={0.5}
                isAnimationActive={false}
                legendType="none"
              />
            )}
            {!isSeriesHidden(TEMP_MAIN_SERIES) && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="temperature_display"
                stroke="#ffb347"
                dot={false}
                strokeWidth={2}
                name="Temperature ε=0.875"
              />
            )}
            {!isSeriesHidden(TEMP_UPPER_SERIES) && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="temperature_upper"
                stroke="#ffd089"
                dot={false}
                strokeWidth={1.5}
                strokeDasharray="5 3"
                name="Temperature ε=0.95"
              />
            )}
            {!isSeriesHidden(TEMP_LOWER_SERIES) && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="temperature_lower"
                stroke="#ff8a3d"
                dot={false}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                name="Temperature ε=0.8"
              />
            )}
            {drawnLines.map((line, idx) => (
              isSeriesHidden(`drawn:${line.id}`) ? null : (
                <Line
                  key={line.id}
                  yAxisId="left"
                  type="monotone"
                  data={line.points}
                  dataKey="temperature"
                  stroke={line.color || DRAW_COLORS[idx % DRAW_COLORS.length]}
                  dot={false}
                  strokeWidth={2}
                  strokeDasharray="2 2"
                  isAnimationActive={false}
                  name={`Drawn line ${idx + 1}`}
                />
              )
            ))}
            {!isSeriesHidden(VISCOSITY_SERIES) && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="viscosity"
                stroke="#6cb6ff"
                dot={false}
                strokeWidth={2}
                name="Viscosity log10(η / dPa·s)"
              />
            )}
            {zoomSelection ? (
              <ReferenceArea
                x1={zoomSelection.start}
                x2={zoomSelection.end}
                yAxisId="left"
                strokeOpacity={0.35}
                stroke="#4fb5ff"
                fill="#4fb5ff"
                fillOpacity={0.16}
              />
            ) : null}
          </ComposedChart>
          </ResponsiveContainer>
          </div>
          <div className="plot-html-legend" aria-label="Center line chart legend">
            {legendItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`plot-html-legend-item plot-html-legend-button${
                  isSeriesHidden(item.id) ? " is-muted" : ""
                }`}
                aria-pressed={!isSeriesHidden(item.id)}
                onClick={() => toggleSeries(item.id)}
              >
                <span
                  className="plot-html-legend-swatch"
                  style={{
                    borderTopColor: item.color,
                    borderTopStyle: item.dash === "solid" ? "solid" : "dashed"
                  }}
                />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
